/**
 * AI mentoring program and custom accessibility work (AXAI-SPEC-018 §6.1, §9; SPEC-019 R-18 CTA
 * pages). Plain-language wording for /ai-mentoring and /custom-accessibility.
 *
 * NDIS registration: applied for on 28 September 2026 and in progress. Change ONLY this constant when
 * that changes; every page reads it, so no page can claim registration on its own.
 */

export const NDIS_REGISTRATION_STATUS = 'applied for, with the application in progress';

/** SPEC-018 §9: pricing is published when the program opens; funding depends on the person's plan. */
export const MENTORING_FUNDING_NOTE =
  'Pricing is published when the program opens; whether a plan can fund it is checked with you first.';

// R-32f: the one place the hourly rate is written. Wording is the artboards'
// ("$220 per hour incl. GST" — Main.dc.html, WhatWeDo.dc.html, Annette.dc.html, Contact.dc.html);
// the figure is unchanged.
//
// 22 Oct 2026: Annette's ruling - the pricing is correct as published and is not to be changed.
export const CONSULTING_RATE = '$220 per hour incl. GST';

/** The ideas day, from Main.dc.html / WhatWeDo.dc.html / Annette.dc.html. R-32f: written once. */
export const IDEAS_DAY_PRICE = '$1,100 incl. GST';

export interface MentoringStage {
  stage: number;
  name: string;
  /** What the person works towards, in plain language. */
  goal: string;
  /** What we decide together at the end of the stage. */
  next: string;
}

export const MENTORING_STAGES: MentoringStage[] = [
  {
    stage: 0,
    name: 'Getting started',
    goal: 'You choose one goal that matters to you and a first task. You choose how you like to communicate, and who supports you. You agree to what we record.',
    next: 'Together we decide if the program is right for you, or if something else would suit you better.',
  },
  {
    stage: 1,
    name: 'Your own tools',
    goal: 'You use the tools you pick for one task that matters to you, with help when you want it.',
    next: 'Which tools fit you, and what gets in the way.',
  },
  {
    stage: 2,
    name: 'Directing the AI',
    goal: 'You start a task with AI, check what it made, and fix it when it is wrong.',
    next: 'Keep going, change the plan, or stop.',
  },
  {
    stage: 3,
    name: 'A skill you can rely on',
    goal: 'You repeat a useful task to an agreed standard, with a support plan, for your own goal.',
    next: 'Some people choose to take a skill towards paid work, with any employer; that is the person’s decision and separate from the program.',
  },
];

export const SESSION_POINTS = [
  'One session works on your goal, in your chosen way of communicating.',
  'You practise a real task, with your mentor beside you (in person or online).',
  'You stay in control: you choose, you check, you decide.',
  'We write down what you tried and what you decided, and you can see it.',
  'Between sessions you can practise, with support if you want it.',
  'At review points we decide together whether to continue, change or stop.',
];

export const AI_ACCESS_POINTS = [
  'During the program you can try more than one AI tool inside a supported workspace.',
  'That access is part of the program, for the length of the program.',
  'If a tool or device of your own would suit you after the program, we look at that with you separately, after the program.',
];

export const CUSTOM_OFFERS = [
  {
    name: 'Accessible AI tools',
    text: 'Tools shaped around how a person or team actually works: easier modes, choice of text, audio or pictures, and the person staying in control.',
  },
  {
    name: 'Accessible software',
    text: 'New software, or changes to what you have, so more people can use it: keyboard and screen reader support, plain language and predictable pages.',
  },
  {
    name: 'Accessibility audits',
    // 22 Oct 2026 (AXAI-SEO-002): /services/accessibility-audit was retired and answers 308, and
    // /what-we-do does not promise a signed report - it says "audits and fixes to WCAG 2.2 AA,
    // tested automatically and by people who use assistive technology". Claim only that.
    text: 'We check your website, app or documents against accessibility standards and tell you what to fix first. See [digital accessibility at aXai](/what-we-do#work).',
  },
];
